import { Dispatch, MutableRefObject, RefObject, SetStateAction } from "react";
import Table, {
  RowAction,
  SortOrderParams,
} from "../../ui-lib/components/Tables/Table";
import { Column, Spacer } from "../Layout/Layout";
import { TableRowSelectionPopupActions } from "../../ui-lib/components/Tables/TableRowSelectionPopup";

import NoItems from "../../ui-lib/components/Tables/NoItems";
import TableCell from "../../ui-lib/components/Tables/TableCell";
import useTranslations from "../../core/i18n/useTranslations";

import {
  AntennaDirection,
  IObservationIdentifier,
} from "../../core/api/observationIdentifiers/types";
import useUser from "../../core/user/useUser";

const columns = (t: (key: string) => string) => [
  {
    key: "MacAddress",
    header: t("Locations:oi_labels_mac_address"),
    fieldTemplate: (rowData: IObservationIdentifier) => {
      return <TableCell value={rowData.macAddress} />;
    },
    sortByField: "MAC",
  },
  {
    key: "AntennaDirection",
    header: t("Locations:oi_labels_antenna_direction"),
    style: { width: "125px" },
    fieldTemplate: (rowData: IObservationIdentifier) => {
      let iconName, leftIconTooltip;
      if (rowData.antennaDirection === AntennaDirection.AntIn) {
        iconName = "arrow-circle-in";
        leftIconTooltip = t("Locations:oi_tooltips_antenna_in");
      } else if (rowData.antennaDirection === AntennaDirection.AntOut) {
        iconName = "arrow-circle-out";
        leftIconTooltip = t("Locations:oi_tooltips_antenna_out");
      }
      return (
        <TableCell
          value={rowData.antennaDirection}
          leftIcon={iconName}
          leftIconTooltip={leftIconTooltip}
        />
      );
    },
  },
];
const extendedColumns = (t: (key: string) => string) => [
  ...columns(t),
  {
    key: "RoomDescription",
    header: t("Locations:oi_labels_room_description"),
    fieldTemplate: (rowData: IObservationIdentifier) => {
      return <TableCell value={rowData.description} />;
    },
    sortByField: "DESCRIPTION",
  },
  {
    key: "OrganizationName",
    header: t("Locations:oi_labels_organization_name"),
    fieldTemplate: (rowData: IObservationIdentifier) => {
      return <TableCell value={rowData.organizationName} />;
    },
    sortByField: "ORGANIZATION",
  },
];

const ObservationIdentifiersTable = ({
  clearSelectedFunction,
  selectedItemRows,
  data,
  tableRowSelectionPopupRef,
  searchState,
  totalItems,
  pageSettings,
  sortParams,
  setIsDeleteModalOpen,
  setIsEditModalOpen,
  onPageSettingsChange,
  setSortParams,
  resetPageSettings,
  extended = false,
}: {
  clearSelectedFunction: MutableRefObject<Function | undefined>;
  selectedItemRows: MutableRefObject<IObservationIdentifier[]>;
  data: IObservationIdentifier[];
  tableRowSelectionPopupRef: RefObject<
    TableRowSelectionPopupActions<Record<string, any>>
  >;
  searchState: any;
  totalItems: number;
  pageSettings: {
    page: number;
    pageSize: number;
  };
  sortParams?: SortOrderParams;
  setIsDeleteModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsEditModalOpen: Dispatch<SetStateAction<boolean>>;
  onPageSettingsChange: (args: { page: number; pageSize: number }) => void;
  setSortParams: (args?: SortOrderParams | undefined) => void;
  resetPageSettings: () => void;
  extended?: boolean;
}) => {
  const t = useTranslations();
  const user = useUser();
  const setClearItemsFunction = (func: Function) => {
    clearSelectedFunction.current = func;
  };
  const isFilterApplied = !!searchState.q?.trim();
  const tableName = extended
    ? "table-observation-identifiers"
    : "table-location-observation-identifiers";

  const rowActions: RowAction<IObservationIdentifier>[] = [
    {
      icon: "pencil-alt",
      text: t("Common:label_edit"),
      onClick: async (rowData) => {
        selectedItemRows.current = [rowData];
        setIsEditModalOpen(true);
      },
    },
  ];

  if (user.config?.show.includes("DeleteKnownLocations")) {
    rowActions.push({
      icon: "trash",
      text: t("Common:delete"),
      iconVariant: "secondary",
      onClick: async (rowData) => {
        selectedItemRows.current = [rowData];
        setIsDeleteModalOpen(true);
      },
    });
  }

  return (
    <Column type="top" className="w-100">
      <Spacer size={24} />
      {data.length > 0 ? (
        <Table<IObservationIdentifier>
          withRowSelection
          withLazyLoading
          rowActionsFixed
          withPagination
          showRowHover
          hideEmptyMessage
          showRowsActionsOnHover
          tableName={tableName}
          columns={extended ? extendedColumns(t) : columns(t)}
          setClearItemsFunction={setClearItemsFunction}
          items={data}
          rowActionsColWidth={64}
          rowActions={rowActions}
          sortParams={sortParams}
          onSortChange={(params) => {
            resetPageSettings();
            setSortParams(params);
          }}
          paginatedItems={{
            items: data,
            pagination: {
              offset: pageSettings.pageSize * (pageSettings.page - 1),
              limit: pageSettings.pageSize,
              total: totalItems,
            },
          }}
          onPageChange={(nextPage) => {
            const page = Math.floor(nextPage.offset / nextPage.limit);

            if (!Number.isNaN(page) && nextPage.limit) {
              onPageSettingsChange({
                page: page + 1,
                pageSize: nextPage.limit,
              });
            }
          }}
          onRowSelectionChange={(selectedItems) => {
            tableRowSelectionPopupRef.current?.open(selectedItems);
          }}
        />
      ) : (
        <NoItems
          title={
            isFilterApplied
              ? t("Table:no_results_found")
              : t("Table:noresult_title")
          }
          icon={isFilterApplied ? undefined : "eye-off"}
          subTitle={
            isFilterApplied
              ? t("Table:adjust_filter_description")
              : t("Table:noresult_subtitle")
          }
          clear={undefined}
        />
      )}
    </Column>
  );
};

export default ObservationIdentifiersTable;
